import React from "react";
import { getSoilMoistureGaugeColor } from "../utils/soilMoistureColor";

// =============================================================================
// Stauden-Slide: Special slide for Blütensteppe (perennial plant bed)
// =============================================================================

// Hardcoded info about the perennial species (from MGS PDF page 3)
// The PDF mentions "u.a. 18 heimischen Arten" but only lists these 8 explicitly
const PERENNIAL_SPECIES = [
  { name: "Gold-Garbe", latin: "Achillea filipendulina 'Coronation Gold'", color: "gelb" },
  { name: "Eberraute", latin: "Artemisia abrotanum", color: "grün" },
  { name: "Strand-Beifuß", latin: "Artemisia maritima", color: "grau-grün" },
  { name: "Büschel-Glockenblume", latin: "Campanula glomerata", color: "blau/violett" },
  { name: "Berg-Reitgras", latin: "Calamagrostis spec. 'Waldenbuch'", color: "grün" },
  { name: "Vielfarbige Wolfsmilch", latin: "Euphorbia polychroma", color: "gelb/grün" },
  { name: "Blauminze", latin: "Nepeta x faassenii 'Walkers Low'", color: "blau" },
  { name: "Rainfarn", latin: "Tanacetum vulgare", color: "gelb" },
];

function getStaudenMetrics(tree) {
  const installation =
    tree?.installations?.find((inst) => !inst?.removed_at) || tree?.installations?.[0];
  const sensor = installation?.sensor;
  const soilSensor = sensor?.soil_moisture_sensor;
  const isUnknown =
    sensor?.sensor_state === 3 || tree?.health_state === 3 || soilSensor?.soil_state === "UN";
  const moistureVal =
    !isUnknown && soilSensor?.soil_moisture != null ? Math.round(soilSensor.soil_moisture) : null;

  const moistureColor = getSoilMoistureGaugeColor(tree, moistureVal);

  return {
    moistureVal,
    depth: installation?.depth != null ? `${installation.depth} cm` : null,
    moistureColor,
  };
}

export default function StaudenSlide({
  tree,
  sensorInfo,
  slideIndex,
  totalSlides,
  progressPct,
  onGoTo,
}) {
  const treeName = tree?.name || "Heimische Blütensteppe";
  const { moistureVal, depth, moistureColor } = getStaudenMetrics(tree);
  const moisturePct = moistureVal != null ? Math.max(0, Math.min(100, moistureVal)) : 0;
  const circumference = 2 * Math.PI * 48;
  const moistureGaugeOffset = circumference * (1 - moisturePct / 100);
  const waterDemandValue = "100-200l";

  return (
    <div className="dv3-slide dv3-slide--stauden">
      <div className="dv3-slide__header">
        <div className={`dv2-sensor-badge dv2-sensor-badge--${sensorInfo?.type || "soil"}`}>
          <span className="dv2-s-dot" />
          {sensorInfo?.label || "FDR-Bodensensor aktiv"}
        </div>
        <div className="dv2-nav-dots">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`dv2-nd ${i === slideIndex ? "dv2-nd--on" : ""}`}
              onClick={() => onGoTo(i)}
              aria-label={`Folie ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="dv3-slide__title-row">
        <div className="dv3-slide__title-content">
          <div className="dv3-slide__tree-name">{treeName}</div>
          <div className="dv3-slide__tree-species">
            Enthaltene Arten (Auswahl){depth ? ` · Sensor in ${depth}` : ""}
          </div>
        </div>
        <div className="dv3-slide__metrics-circles">
          <div className="dv3-circle">
            <svg width="100" height="100" viewBox="0 0 116 116">
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke="rgba(14,29,44,0.08)"
                strokeWidth="9"
              />
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke={moistureColor}
                strokeWidth="9"
                strokeDasharray={circumference}
                strokeDashoffset={moistureGaugeOffset}
                strokeLinecap="round"
                transform="rotate(-90 58 58)"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
              />
              <text
                x="58"
                y="50"
                textAnchor="middle"
                fill="#0E1D2C"
                fontFamily="Syne,sans-serif"
                fontSize="22"
                fontWeight="700"
              >
                {moistureVal != null ? `${moistureVal}%` : "–"}
              </text>
              <text x="58" y="66" textAnchor="middle" fill="#6B8FA4" fontSize="9">
                Bodenfeuchte
              </text>
            </svg>
          </div>
          <div className="dv3-circle">
            <svg width="100" height="100" viewBox="0 0 116 116">
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke="rgba(14,29,44,0.08)"
                strokeWidth="9"
              />
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke={moistureColor}
                strokeWidth="9"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * 0.3}
                strokeLinecap="round"
                transform="rotate(-90 58 58)"
              />
              <text
                x="58"
                y="50"
                textAnchor="middle"
                fill="#0E1D2C"
                fontFamily="Syne,sans-serif"
                fontSize="16"
                fontWeight="700"
              >
                {waterDemandValue}
              </text>
              <text x="58" y="66" textAnchor="middle" fill="#6B8FA4" fontSize="8">
                Bewässerungsbedarf
              </text>
            </svg>
          </div>
        </div>
      </div>

      <div className="dv3-slide__body">
        {/* Center: Image of Blütensteppe */}
        <div className="dv3-center">
          <div className="dv3-center__layer" style={{ opacity: 1 }}>
            <img
              src="/img/trees/2026_bluetensteppe_rechteckig.png"
              alt="Heimische Blütensteppe"
              className="dv3-center__image"
            />
          </div>
          <div className="dv3-center__indicator">
            <div className="dv3-center__tab dv3-center__tab--active">Foto</div>
          </div>
        </div>

        {/* Right: Species list */}
        <div className="dv3-info">
          <div className="dv3-stauden-arten">
            <div className="dv3-stauden-grid">
              {PERENNIAL_SPECIES.map((sp, idx) => (
                <div key={idx} className="dv3-stauden-item-compact">
                  <div className="dv3-stauden-item__name">{sp.name}</div>
                  <div className="dv3-stauden-item__latin">
                    <em>{sp.latin}</em>
                  </div>
                  <div className="dv3-stauden-item__color">{sp.color}</div>
                </div>
              ))}
            </div>
            <div className="dv3-stauden-note">+ 10 weitere heimische Arten</div>
          </div>
        </div>
      </div>

      <div className="dv2-prog-bar" style={{ width: `${progressPct}%` }} />
    </div>
  );
}
